import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useNotification } from '../hooks/useNotification';
import axiosInstance from '../lib/axios';
import {
  ActivateSystemPromptVersionInput,
  CreateSystemPromptVersionInput,
  SystemPrompt,
  SystemPromptVersion,
} from '../types/admin';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export const systemPromptKeys = {
  all: ['systemPrompts'] as const,
  lists: () => [...systemPromptKeys.all, 'list'] as const,
  details: () => [...systemPromptKeys.all, 'detail'] as const,
  detail: (key: string) => [...systemPromptKeys.details(), key] as const,
  versions: (key: string) => [...systemPromptKeys.all, 'versions', key] as const,
};

const basePath = (key: string) => `/v1/admin/system-prompts/${encodeURIComponent(key)}`;

const fetchSystemPrompts = async (): Promise<SystemPrompt[]> => {
  const response = await axiosInstance.get<ApiResponse<SystemPrompt[]>>('/v1/admin/system-prompts');
  return response.data.data || [];
};

const fetchSystemPromptVersions = async (key: string): Promise<SystemPromptVersion[]> => {
  const response = await axiosInstance.get<ApiResponse<SystemPromptVersion[]>>(
    `${basePath(key)}/versions`
  );
  return response.data.data || [];
};

const createSystemPromptVersion = async ({
  key,
  ...input
}: CreateSystemPromptVersionInput): Promise<SystemPromptVersion> => {
  const response = await axiosInstance.post<ApiResponse<SystemPromptVersion>>(
    `${basePath(key)}/versions`,
    input
  );
  return response.data.data as SystemPromptVersion;
};

const activateSystemPromptVersion = async ({
  key,
  version,
}: ActivateSystemPromptVersionInput): Promise<SystemPrompt> => {
  const response = await axiosInstance.post<ApiResponse<SystemPrompt>>(
    `${basePath(key)}/versions/${version}/activate`
  );
  return response.data.data as SystemPrompt;
};

export const useSystemPrompts = () => {
  return useQuery({
    queryKey: systemPromptKeys.lists(),
    queryFn: fetchSystemPrompts,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useSystemPromptVersions = (key: string | null) => {
  return useQuery({
    queryKey: systemPromptKeys.versions(key ?? ''),
    queryFn: () => fetchSystemPromptVersions(key as string),
    enabled: !!key,
  });
};

const errorMessage = (error: AxiosError<ApiResponse>, fallback: string) =>
  error.response?.data?.message || error.response?.data?.error || fallback;

export const useCreateSystemPromptVersion = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: createSystemPromptVersion,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: systemPromptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: systemPromptKeys.versions(data.key) });
      notification.success(`Version ${data.version} created. Activate it to use it at runtime.`);
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(errorMessage(error, 'Failed to create system prompt version'));
    },
  });
};

export const useActivateSystemPromptVersion = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: activateSystemPromptVersion,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: systemPromptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: systemPromptKeys.versions(data.key) });
      notification.success(`Version ${data.activeVersion} is now active`);
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(errorMessage(error, 'Failed to activate system prompt version'));
    },
  });
};
